import { useCallback, useEffect, useRef, useState } from 'react';
import type { Bbox, ImageTransform } from '../useImageTransform';
import { MIN_EDGE_PX } from './useBoxDraw';

/**
 * Pointer state machine for DRAWING a brand-new bbox.
 *
 * Where {@link useBoxDraw} edits an existing box, this one captures
 * a single pointerdown → move → up gesture and builds a fresh bbox
 * from the two endpoints. Used by the manual-add flow so a user
 * can draw around an item Vision missed.
 *
 * Contract:
 *   - caller mounts a full-area pointer catcher; every onPointerDown
 *     invokes `startDraw(event)`
 *   - the hook attaches document-level pointermove/up listeners
 *     while a draft is in flight — so the drag tracks even when the
 *     pointer leaves the catcher
 *   - `draft` is an imageSpace bbox while in flight; null otherwise
 *   - on pointerup, the hook calls `onCommit(bbox)` ONCE if the
 *     bbox has meaningful area (both sides >= MIN_EDGE_PX); if it's
 *     a stray tap, `onCancel` fires instead
 *   - `reset()` clears any in-flight draft (e.g. user Esc'd)
 *
 * Clamps to the image's natural dimensions on every move. A bbox
 * that starts at the image edge and drags outside stays pinned at
 * the edge instead of overflowing.
 *
 * Returns `isDrawing` so the caller can suppress other interactions
 * (e.g. tab-bar clicks) while the user is mid-gesture.
 */

export interface PointerEventLike {
  clientX: number;
  clientY: number;
  pointerId: number;
  pointerType?: string;
  currentTarget?: { setPointerCapture?: (id: number) => void } | null;
}

export interface UseBoxDraftOptions {
  /** Called once with the final imageSpace bbox when the user
   *  releases a meaningful-size draft. */
  onCommit: (
    bbox: Bbox,
    meta: {
      draw_duration_ms: number;
      device_kind: 'mouse' | 'touch' | 'pen';
    },
  ) => void;
  /** Called when the user's gesture ended without producing a
   *  usable bbox — tiny tap, Esc, pointercancel.
   *
   *  When `onTap` is set, a too-small gesture that's ALSO under the
   *  tap-duration threshold is routed to `onTap` instead of
   *  `onCancel` — the AI tap-add path (#411). Cancellation reasons
   *  other than 'too-small' (Esc, pointer-cancel) always go here. */
  onCancel?: (reason: 'too-small' | 'escape' | 'pointer-cancel') => void;
  /** AI tap-add (#411). When provided, a too-small gesture under the
   *  tap-duration threshold (default 250ms) and under ~6px of
   *  movement fires `onTap(anchor, meta)` with the image-space anchor
   *  point instead of `onCancel('too-small')`. The caller is expected
   *  to manufacture a centred square bbox around the anchor and POST
   *  it to `/inventory/manual-analyze`. */
  onTap?: (
    anchor: { x: number; y: number },
    meta: {
      draw_duration_ms: number;
      device_kind: 'mouse' | 'touch' | 'pen';
    },
  ) => void;
  /** Override the tap-duration ceiling. Defaults to 250ms. */
  tapMaxDurationMs?: number;
  /** Fires when the user taps OUTSIDE the natural image (the
   *  letterbox dead-zone on `object-fit: contain`). The bbox can't
   *  be computed so the gesture is dropped — surfacing this lets
   *  the caller show a "tap on the photo" toast instead of silently
   *  doing nothing. */
  onTapMiss?: () => void;
}

export interface UseBoxDraftResult {
  draft: Bbox | null;
  isDrawing: boolean;
  /** Caller wires this to the catcher's `onPointerDown`. */
  startDraw: (event: PointerEventLike) => void;
  /** Force-clear any in-flight draft. */
  reset: () => void;
}

interface DraftState {
  pointerId: number;
  pointerType: 'mouse' | 'touch' | 'pen';
  t0: number;
  /** imageSpace anchor point the user pressed at. */
  anchor: { x: number; y: number };
}

export function useBoxDraft(
  transform: ImageTransform,
  containerRef: React.RefObject<HTMLElement | null>,
  opts: UseBoxDraftOptions,
): UseBoxDraftResult {
  const [draft, setDraft] = useState<Bbox | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const stateRef = useRef<DraftState | null>(null);
  // Mirror of `draft` for document listeners — prevents the effect
  // from re-binding on every pointermove (React's dep array can't
  // depend on `draft` without thrashing 4 addEventListener calls on
  // every frame of the drag).
  const draftRef = useRef<Bbox | null>(null);
  // Store the latest commit/cancel callbacks in a ref so the
  // document listeners don't re-bind on every parent render.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const writeDraft = useCallback((next: Bbox | null) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  const pointerToImage = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const el = containerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const dom = { x: clientX - rect.left, y: clientY - rect.top };
      return transform.toImage(dom);
    },
    [containerRef, transform],
  );

  const getBounds = useCallback(
    (): { w: number; h: number } | null => {
      const sz = transform.imageSize;
      if (!sz) return null;
      return { w: sz.width, h: sz.height };
    },
    [transform],
  );

  const reset = useCallback(() => {
    stateRef.current = null;
    writeDraft(null);
    setIsDrawing(false);
  }, [writeDraft]);

  const startDraw = useCallback(
    (event: PointerEventLike) => {
      const img = pointerToImage(event.clientX, event.clientY);
      const bounds = getBounds();
      // Letterbox dead-zone: the click hit the container but not the
      // natural image (object-fit: contain leaves bars). Surface a
      // miss so the caller can prompt the user toward the photo.
      if (!img || !bounds) {
        optsRef.current.onTapMiss?.();
        return;
      }
      // Also miss when the tap lands *inside* the container but
      // *outside* the image bounds (toImage can return a point past
      // 0..w/h on the letterbox edges depending on transform impl).
      if (img.x < 0 || img.y < 0 || img.x > bounds.w || img.y > bounds.h) {
        optsRef.current.onTapMiss?.();
        return;
      }
      const anchor = {
        x: clamp(img.x, 0, bounds.w),
        y: clamp(img.y, 0, bounds.h),
      };
      stateRef.current = {
        pointerId: event.pointerId,
        pointerType: normalizeDevice(event.pointerType),
        t0: performance.now(),
        anchor,
      };
      // Seed with a zero-size rect so the visual shows immediately.
      writeDraft({ x: anchor.x, y: anchor.y, w: 0, h: 0 });
      setIsDrawing(true);
      try {
        event.currentTarget?.setPointerCapture?.(event.pointerId);
      } catch {
        /* pointer-capture unsupported — fall through to doc listeners */
      }
    },
    [pointerToImage, getBounds],
  );

  useEffect(() => {
    if (!isDrawing) return;

    const handleMove = (e: PointerEvent) => {
      const st = stateRef.current;
      if (!st || e.pointerId !== st.pointerId) return;
      const img = pointerToImage(e.clientX, e.clientY);
      if (!img) return;
      const bounds = getBounds();
      if (!bounds) return;
      // The live point can drag outside the image — clamp before
      // computing the rect so we never render a bbox outside the
      // natural dimensions.
      const live = {
        x: clamp(img.x, 0, bounds.w),
        y: clamp(img.y, 0, bounds.h),
      };
      writeDraft(rectFromEndpoints(st.anchor, live));
    };

    const handleUp = (e: PointerEvent) => {
      const st = stateRef.current;
      if (!st || e.pointerId !== st.pointerId) return;
      const img = pointerToImage(e.clientX, e.clientY);
      const bounds = getBounds();
      // Fall back to the last live draft (read via ref — state may
      // not yet be flushed) if the up-event's coords can't convert.
      const final =
        img && bounds
          ? rectFromEndpoints(st.anchor, {
              x: clamp(img.x, 0, bounds.w),
              y: clamp(img.y, 0, bounds.h),
            })
          : draftRef.current;
      const now = performance.now();
      stateRef.current = null;
      setIsDrawing(false);
      writeDraft(null);
      const duration = Math.max(0, Math.round(now - st.t0));
      if (final && final.w >= MIN_EDGE_PX && final.h >= MIN_EDGE_PX) {
        optsRef.current.onCommit(final, {
          draw_duration_ms: duration,
          device_kind: st.pointerType,
        });
      } else {
        // Too-small gesture. Route to onTap if available + quick
        // enough; otherwise the legacy onCancel('too-small') path.
        const tapCap = optsRef.current.tapMaxDurationMs ?? 250;
        if (optsRef.current.onTap && duration <= tapCap) {
          optsRef.current.onTap(
            { x: st.anchor.x, y: st.anchor.y },
            {
              draw_duration_ms: duration,
              device_kind: st.pointerType,
            },
          );
        } else {
          optsRef.current.onCancel?.('too-small');
        }
      }
    };

    const handleCancel = (e: PointerEvent) => {
      const st = stateRef.current;
      if (!st || e.pointerId !== st.pointerId) return;
      stateRef.current = null;
      writeDraft(null);
      setIsDrawing(false);
      optsRef.current.onCancel?.('pointer-cancel');
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!stateRef.current) return;
      stateRef.current = null;
      writeDraft(null);
      setIsDrawing(false);
      optsRef.current.onCancel?.('escape');
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleCancel);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleCancel);
      document.removeEventListener('keydown', handleKey);
    };
    // `draft` is intentionally NOT in the dep array — the listeners
    // read the live value via `draftRef` so they don't re-bind on
    // every pointermove frame.
  }, [isDrawing, pointerToImage, getBounds, writeDraft]);

  return { draft, isDrawing, startDraw, reset };
}

/**
 * Build a normalized (x, y, w, h) bbox from two endpoints. Exported
 * for unit tests — coords can be given in any direction (user drags
 * from bottom-right to top-left).
 */
export function rectFromEndpoints(
  a: { x: number; y: number },
  b: { x: number; y: number },
): Bbox {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.abs(b.x - a.x);
  const h = Math.abs(b.y - a.y);
  return { x, y, w, h };
}

const clamp = (n: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, n));

const normalizeDevice = (
  raw: string | undefined,
): 'mouse' | 'touch' | 'pen' => {
  if (raw === 'touch') return 'touch';
  if (raw === 'pen') return 'pen';
  return 'mouse';
};
